import React from "react";

import DotsHorizontal from "@uui/DotsHorizontal";
import TrashIcon from "@uui/Trash02";

import Divider from "@mui/material/Divider";

import HeaderButton from "@app/analytics/components/reports/HeaderButton";
import useMeetingReport from "@app/analytics/MeetingReport/hooks/useMeetingReport";
import useSessionMetadata from "@app/analytics/MeetingReport/hooks/useSessionMetadata";
import DownloadMenu from "@app/analytics/MeetingReport/MeetingReportHeader/DownloadMenu";
import ExternalShareMenu from "@app/analytics/MeetingReport/MeetingReportHeader/ExternalShareMenu";
import ManageReportTags from "@app/analytics/MeetingReport/MeetingReportHeader/ManageReportTags";
import ParticipantsList from "@app/analytics/MeetingReport/MeetingReportHeader/ParticipantsList";
import RenameReport from "@app/analytics/MeetingReport/MeetingReportHeader/ReportActionsMenu/RenameReport";
import useEditStore from "@app/analytics/MeetingReport/stores/edits";
import useMeetingReportUiStore from "@app/analytics/MeetingReport/stores/ui";
import { MeetingReportDialog } from "@app/analytics/MeetingReport/types";
import * as ShareUtils from "@app/common/access/ShareReport/utils";
import {
  DownloadIcon,
  ExternalLinkIcon,
  TagIcon,
  UsersIcon,
} from "@app/common/assets/icons";
import useAuth from "@app/common/auth/useAuth";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import useResponsiveView from "@app/common/hooks/useResponsiveView";
import { EntityAccessLevel } from "@app/common/types";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

import Menu from "@rui/Menu";
import MenuItem from "@rui/MenuItem";
import { Column } from "@rui/Stack";

import CopyLinkMenuItem from "./CopyLinkMenuItem";
import DeleteReportConfirmationModal from "./DeleteReportConfirmationModal";
import RemoveReportConfirmationModal from "./RemoveReportConfirmationModal";
import * as HeaderUtils from "../utils";

const ReportActionsMenu = ({
  acl,
  disabled,
}: {
  acl: EntityAccessLevel;
  disabled: boolean;
}) => {
  const { anchorEl, onClick, onClose: onMenuClose, open } = useAnchorElement();

  const { inDesktopView } = useResponsiveView();
  const { canAccessMeetingDownloads } = useFeatureAccess();

  const setDialog = useMeetingReportUiStore((state) => state.setDialog);
  const inEditMode = useEditStore((state) => state.editingSection != null);

  const { user } = useAuth();
  const { data: sessionMetadata } = useSessionMetadata();
  const { meetingReport } = useMeetingReport();

  const canRenameReport = useEditStore(
    (state) => state.editAccessLevel === "full"
  );

  const numParticipants = meetingReport?.participants?.length ?? 0;
  const showParticipants = HeaderUtils.showParticipants({
    acl,
    numParticipants,
    sessionMetadata,
  });
  const showTags = HeaderUtils.showTags({ sessionMetadata });
  const showDownloads = HeaderUtils.showDownloadMenu({
    canDownload: canAccessMeetingDownloads,
    sessionMetadata,
  });

  const canShareReport = ShareUtils.canShareReport(acl);
  const actionsDisabled = inEditMode || sessionMetadata.isProcessing;

  const openDialog = (dialog: MeetingReportDialog) => () => {
    onMenuClose();
    setDialog(dialog);
  };

  return (
    <>
      <HeaderButton
        className="meeting-report__header__actions"
        Icon={<DotsHorizontal />}
        onClick={onClick}
        disabled={disabled}
      />
      <Menu anchorEl={anchorEl} open={open} onClose={onMenuClose}>
        {!inDesktopView && (
          <Column display="contents" onClick={onMenuClose}>
            {showDownloads && (
              <MenuItem
                disabled={disabled}
                icon={<DownloadIcon />}
                onClick={openDialog("downloads")}
              >
                Download
              </MenuItem>
            )}
            <CopyLinkMenuItem sessionId={sessionMetadata.sessionId} />
            {Boolean(user) && (
              <MenuItem
                icon={<ExternalLinkIcon />}
                onClick={openDialog("external_share_select")}
                disabled={actionsDisabled}
              >
                Push to...
              </MenuItem>
            )}
            <Divider />
            {showTags && (
              <MenuItem icon={<TagIcon />} onClick={openDialog("tags")}>
                View Tags ({sessionMetadata.tags.length})
              </MenuItem>
            )}
            {showParticipants && (
              <MenuItem
                icon={<UsersIcon />}
                onClick={openDialog("participants")}
              >
                View Participants ({numParticipants})
              </MenuItem>
            )}
            {(showTags || showParticipants) && <Divider />}
          </Column>
        )}
        {Boolean(user) && (
          <Column display="contents" onClick={onMenuClose}>
            {canRenameReport && (
              <>
                <RenameReport disabled={inEditMode} />
                {inDesktopView && <Divider />}
              </>
            )}
            {sessionMetadata.currentUserIsMeetingOwner ? (
              <MenuItem
                icon={<TrashIcon />}
                disabled={actionsDisabled}
                onClick={openDialog("delete_report")}
                type="error"
              >
                Delete Report
              </MenuItem>
            ) : (
              <MenuItem
                icon={<TrashIcon />}
                disabled={actionsDisabled}
                onClick={openDialog("remove_report")}
                type="error"
              >
                Remove Report
              </MenuItem>
            )}
          </Column>
        )}
      </Menu>

      {/* Dialogs */}
      {showDownloads && <DownloadMenu variant="dialog" />}
      {showParticipants && <ParticipantsList variant="dialog" />}
      {showTags && <ManageReportTags variant="dialog" />}
      {sessionMetadata.currentUserIsMeetingOwner ? (
        <DeleteReportConfirmationModal />
      ) : (
        <RemoveReportConfirmationModal />
      )}
      {Boolean(user) && (
        <ExternalShareMenu
          canShareReport={canShareReport}
          inEditMode={inEditMode}
          isProcessing={sessionMetadata.isProcessing}
          sessionId={sessionMetadata.sessionId}
          variant="dialog"
        />
      )}
    </>
  );
};

export default ReportActionsMenu;
